import { useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { trackEvent } from "./analytics";
import {
  markAsRead as markAsReadAction,
  markAsReadLater as markAsReadLaterAction,
  markAsSkipped as markAsSkippedAction,
  markAsUnRead as markAsUnReadAction,
} from "../features/reading/actions";

/**
 *
 * @param {*} comic (with readingId property)
 */
export default function useChangeStatus(readingId) {
  const dispatch = useDispatch();
  const toast = useToast({ duration: 5000, isClosable: true });
  const queryClient = useQueryClient();
  const updateReadingCache = (response) => {
    queryClient.invalidateQueries("paywall");
    if (!response?.data?.data) {
      queryClient.invalidateQueries(["reading", readingId]);
      toast({
        title: "Could not update progress",
        description: "Your saved progress is unchanged. Please try again.",
        status: "error",
      });
      return false;
    }
    queryClient.setQueryData(["reading", readingId], {
      data: response.data.data,
    });
    return true;
  };

  const changeStatus = (action) => dispatch(action).then(updateReadingCache);
  const trackStatusChange = (comicId, status, prevStatus) =>
    trackEvent("comic_status_changed", {
      reading_id: readingId,
      comic_id: comicId,
      status,
      prev_status: prevStatus,
    });
  const markAsUnRead = (comicId, story, skipped, readLater) => {
    trackStatusChange(
      comicId,
      "unread",
      skipped ? "skipped" : readLater ? "read_later" : "read"
    );
    return changeStatus(
      markAsUnReadAction(comicId, story, readingId, skipped, readLater)
    );
  };
  const markAsRead = (comicId, story) => {
    trackStatusChange(comicId, "read", "unread");
    return changeStatus(markAsReadAction(comicId, story, readingId));
  };
  const markAsSkipped = (comicId, story) => {
    trackStatusChange(comicId, "skipped", "unread");
    return changeStatus(markAsSkippedAction(comicId, story, readingId));
  };
  const markAsReadLater = (comicId, story) => {
    trackStatusChange(comicId, "read_later", "unread");
    return changeStatus(markAsReadLaterAction(comicId, story, readingId));
  };
  const markAsUnreadActionOnly = markAsUnRead;
  const markAsReadActionOnly = markAsRead;
  const markAsSkippedActionOnly = markAsSkipped;
  const markAsReadLaterActionOnly = markAsReadLater;

  return {
    markAsRead,
    markAsUnRead,
    markAsSkipped,
    markAsReadLater,
    markAsReadActionOnly,
    markAsSkippedActionOnly,
    markAsReadLaterActionOnly,
    markAsUnreadActionOnly,
  };
}
